import { memo, useMemo } from 'react';

import { Checkbox, Stack } from '@mui/material';
import { SortingRule } from 'react-table';

import { AccessCentreUser } from 'domains/accessCentre/types';
import Table, { ColumnOption, renderDate } from 'domains/core/components/Table';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import { SortDirection } from 'models/enums';

import UsersTableNameCell from './UsersTableNameCell';
import UsersTableRoleSelectionCell from './UsersTableRoleSelectionCell';

/** Matches GET /users `sort` query param field names. */
const defaultSortingRules: SortingRule<object>[] = [{ id: 'last_modified_timestamp', desc: true }];

const noopOnSort = (_columnId: string, _direction: SortDirection) => {};

export type BulkSelectCallbacks = {
    isUserSelected: (id: string) => boolean;
    onToggleUser: (user: AccessCentreUser) => void;
};

type Props = {
    users: AccessCentreUser[];
    isLoading?: boolean;
    showChangeRole?: boolean;
    /** When true and tenant is not platform-integrated, name column shows inline edit on hover. */
    allowInlineUserNameEdit?: boolean;
    /** When set with `showChangeRole`, renders row checkboxes for bulk selection */
    bulkSelect?: BulkSelectCallbacks;
    /** Shown as a page-level success snackbar after a single-user role change from the role column. */
    onUserRoleAssignSuccess?: (message: string) => void;
    onSort?: (sortColumn: string, direction: SortDirection) => void;
};

const UsersTable = ({
    users,
    isLoading,
    showChangeRole = false,
    allowInlineUserNameEdit = false,
    bulkSelect,
    onUserRoleAssignSuccess,
    onSort = noopOnSort,
}: Props) => {
    const data: object[] = useMemo(
        () =>
            users.map((u) => ({
                ...u,
                lastModifiedSort: new Date(u.last_modified_timestamp ?? 0),
            })),
        [users]
    );

    const selectColumn: ColumnOption[] = useMemo(() => {
        if (!showChangeRole || !bulkSelect) {
            return [];
        }
        return [
            {
                id: 'select',
                Header: '',
                accessor: 'id',
                disableSortBy: true,
                align: 'center',
                sx: { width: 52, maxWidth: 52 },
                Cell: (cellProps: any) => {
                    const raw = cellProps.row.original as AccessCentreUser & { lastModifiedSort?: Date };
                    const { lastModifiedSort: _lm, ...user } = raw;
                    return (
                        <Checkbox
                            checked={bulkSelect.isUserSelected(user.id)}
                            onChange={() => bulkSelect.onToggleUser(user as AccessCentreUser)}
                            inputProps={{
                                'aria-label': `Select ${user.name ?? user.email ?? 'user'}`,
                            }}
                            data-testid={`access-center-users-row-select-${user.id}`}
                            size="small"
                            sx={{ p: 0.5 }}
                        />
                    );
                },
            },
        ];
    }, [showChangeRole, bulkSelect]);

    const columns: ColumnOption[] = useMemo(
        () => [
            ...selectColumn,
            {
                id: 'name',
                Header: 'User Name',
                accessor: 'name',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => (
                    <UsersTableNameCell
                        user={row.row.original as AccessCentreUser}
                        allowInlineEdit={allowInlineUserNameEdit}
                    />
                ),
            },
            {
                id: 'email',
                Header: 'Email',
                accessor: 'email',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value ?? ''} />,
            },
            {
                id: 'role_name',
                Header: 'Role',
                accessor: 'role_name',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => (
                    <UsersTableRoleSelectionCell
                        user={row.row.original as AccessCentreUser}
                        showChangeRole={showChangeRole}
                        onRoleAssignSuccess={onUserRoleAssignSuccess}
                    />
                ),
            },
            {
                id: 'last_modified_timestamp',
                Header: 'Date Updated',
                accessor: 'lastModifiedSort',
                sortType: 'datetime',
                Cell: renderDate,
            },
        ],
        [selectColumn, showChangeRole, allowInlineUserNameEdit, onUserRoleAssignSuccess]
    );

    return (
        <Stack
            data-testid={isLoading ? 'access-center-users-table-loading' : 'access-center-users-table'}
            role={isLoading ? 'progressbar' : 'table'}
            aria-label={isLoading ? 'Loading users' : 'Users table'}
        >
            <Table
                columns={columns}
                data={data}
                defaultSortingRules={defaultSortingRules}
                isLoading={isLoading}
                manualSortBy
                onSort={onSort}
                stickyHeader
            />
        </Stack>
    );
};

export default memo(UsersTable);
